import axios from "axios";
import { getValidToken } from "./authService";
import {
  isDemoMode,
  demoGetTransactions,
  demoCreateTransaction,
  demoDeleteTransaction,
  demoGetTransactionById,
  demoGetTransactionByIdFlexible,
  demoUpdateTransaction,
  demoGetRecurring,
  demoGetRecurringByIdFlexible,
  demoUpdateRecurring,
  demoGetUpcomingRecurring,
} from "../demo/demoStore";

const API_URL = process.env.REACT_APP_API_URL;

const apiClient = axios.create({
  baseURL: API_URL,
});

apiClient.interceptors.request.use(
  async (config) => {
    const token = await getValidToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const addContextParams = (params, contextParams) => {
  return {
    ...params,
    context: contextParams.context,
    ...(contextParams.groupId && { groupId: contextParams.groupId }),
  };
};

export const getVendors = async (contextParams) => {
  if (isDemoMode()) return [];
  const params = addContextParams({}, contextParams);
  const response = await apiClient.get(`/vendors-list`, { params });
  console.log(response.data);
  return response.data;
};

export const getTransactions = async (type = "", contextParams) => {
  if (isDemoMode()) return demoGetTransactions(type);
  const endpoint = type ? `/transactions/type/${type}` : "/transactions";
  const params = addContextParams({}, contextParams);
  const response = await apiClient.get(endpoint, { params });
  return response.data;
};

export const deleteTransaction = async (id, contextParams) => {
  if (isDemoMode()) return demoDeleteTransaction(id);
  const params = addContextParams({}, contextParams);
  const response = await apiClient.delete(`/transaction/${id}`, { params });
  return response.data;
};

export const getTransactionById = async (id, contextParams) => {
  if (isDemoMode()) return demoGetTransactionById(id);
  const params = addContextParams({}, contextParams);
  const response = await apiClient.get(`/transaction/${id}`, { params });
  return response.data;
};

export const createTransaction = async (transaction, contextParams) => {
  if (isDemoMode()) return demoCreateTransaction(transaction);
  const payload = {
    ...transaction,
    context_type: contextParams.context,
    group_id: contextParams.groupId || null,
  };
  const response = await apiClient.post("/transaction", payload);
  return response;
};

export const updateTransaction = async (id, transaction, contextParams) => {
  if (isDemoMode()) return demoUpdateTransaction(id, transaction);
  const payload = {
    ...transaction,
    context_type: contextParams.context,
    group_id: contextParams.groupId || null,
  };
  const params = addContextParams({}, contextParams);
  const response = await apiClient.put(`/transaction/${id}`, payload, {
    params,
  });
  return response;
};

export const getTransactionByIdFlexible = async (id) => {
  if (isDemoMode()) return demoGetTransactionByIdFlexible(id);
  const response = await apiClient.get(`/transaction/${id}/flexible`);
  return response.data;
};

//recurring payments

export const getRecurringTransactionByIdFlexible = async (id) => {
  if (isDemoMode()) return demoGetRecurringByIdFlexible(id);
  const response = await apiClient.get(`/recurring/${id}/flexible`);
  return response.data;
};

// export const createRecurringTransaction = async (
//   transaction,
//   contextParams
// ) => {
//   const payload = {
//     ...transaction,
//     context_type: contextParams.context,
//     group_id: contextParams.groupId || null,
//   };
//   const response = await apiClient.post("/recurring", payload);
//   return response;
// };

export const getRecurringTransactions = async (type = "", contextParams) => {
  if (isDemoMode()) return demoGetRecurring(type);
  const endpoint = type ? `/recurring/type/${type}` : "/recurring";
  const params = addContextParams({}, contextParams);
  const response = await apiClient.get(endpoint, { params });
  return response.data;
};

// export const getAllRecurringTransactions = async (contextParams) => {
//   const params = addContextParams({}, contextParams);
//   const response = await apiClient.get("/recurring", { params });
//   return response.data;
// };

export const getRecurringTransactionById = async (id, contextParams) => {
  const params = addContextParams({}, contextParams);
  const response = await apiClient.get(`/recurring/${id}`, { params });
  return response.data;
};

export const deleteRecurringTransaction = async (id, contextParams) => {
  if (isDemoMode()) return Promise.resolve({ success: true });
  const params = addContextParams({}, contextParams);
  const response = await apiClient.delete(`/recurring/${id}`, { params });
  return response;
};

export const updateRecurringTransaction = async (
  id,
  transaction,
  contextParams
) => {
  if (isDemoMode()) return demoUpdateRecurring(id, transaction);
  try {
    const recurringPayload = {
      start_date: transaction.date,
      frequency: transaction.frequency,
      next_due_date: transaction.next_due_date,
      end_date: transaction.isPermanent ? null : transaction.end_date,
      category_id: transaction.category_id,
      amount: parseFloat(transaction.amount),
      vendor: transaction.vendor || "",
      note: transaction.note || "",
      context_type: contextParams.context,
      group_id: contextParams.groupId || null,
      old_groupId: transaction.group_id,
      old_groupName: transaction.group_name,
      found_in_context: transaction.found_in_context,
      number_of_payments: transaction.number_of_payments,
      total_amount: transaction.total_amount,
      ...(transaction.isPermanent
        ? {}
        : { recurring_count: transaction.recurringcount }),
    };

    const params = addContextParams({}, contextParams);
    const response = await apiClient.put(`/recurring/${id}`, recurringPayload, {
      params,
    });
    return response;
  } catch (error) {
    console.error("Error updating recurring transaction:", error);
    throw error;
  }
};

export const changeFromRecurringToTransaction = async (
  id,
  transaction,
  contextParams
) => {
  if (isDemoMode()) return demoUpdateTransaction(id, transaction);
  const payload = {
    ...transaction,
    context_type: contextParams.context,
    group_id: contextParams.groupId || null,
  };
  const params = addContextParams({}, contextParams);
  const response = await apiClient.put(
    `/recurring-to-transaction/${id}`,
    payload,
    { params }
  );
  return response;
};

export const changeFromTransactionToRecurring = async (
  id,
  transaction,
  contextParams
) => {
  if (isDemoMode()) return demoUpdateRecurring(id, transaction);
  const payload = {
    ...transaction,
    context_type: contextParams.context,
    group_id: contextParams.groupId || null,
  };
  const params = addContextParams({}, contextParams);
  const response = await apiClient.put(
    `/transaction-to-recurring/${id}`,
    payload,
    { params }
  );
  return response;
};

export const importTransactions = async (file, contextParams) => {
  const formData = new FormData();
  formData.append("file", file);

  const params = addContextParams({}, contextParams);
  const config = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    params,
  };

  const response = await apiClient.post(
    "/transactions/import",
    formData,
    config
  );
  return response.data;
};

export const exportTransactions = async (type = "", contextParams) => {
  try {
    const token = await getValidToken();

    const params = addContextParams({ type }, contextParams);
    const searchParams = new URLSearchParams(params).toString();

    let url = `${API_URL}/transactions/export`;
    if (searchParams) {
      url += `?${searchParams}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Export failed with status: ${response.status}`);
    }

    const blob = await response.blob();

    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = "transactions.xlsx";
    document.body.appendChild(a);
    a.click();

    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);

    return true;
  } catch (error) {
    console.error("Error exporting transactions:", error);
    throw error;
  }
};

export const getAllRecurringTransactions = async (contextParams) => {
  if (isDemoMode()) return demoGetRecurring();
  const params = addContextParams({}, contextParams);
  const response = await apiClient.get("/recurring", { params });
  return response.data;
};

// Get only active recurring transactions
export const getActiveRecurringTransactions = async (contextParams) => {
  if (isDemoMode()) return demoGetRecurring();
  const params = addContextParams({ status: "active" }, contextParams);
  const response = await apiClient.get("/recurring", { params });
  return response.data;
};

// Pause a recurring payment
export const pauseRecurringPayment = async (id, contextParams) => {
  if (isDemoMode()) return Promise.resolve({ success: true });
  const params = addContextParams({}, contextParams);
  const response = await apiClient.patch(
    `/recurring/${id}/pause`,
    {},
    { params }
  );
  return response.data;
};

// Resume a recurring payment
export const resumeRecurringPayment = async (id, contextParams) => {
  if (isDemoMode()) return Promise.resolve({ success: true });
  const params = addContextParams({}, contextParams);
  const response = await apiClient.patch(
    `/recurring/${id}/resume`,
    {},
    { params }
  );
  return response.data;
};

// Toggle recurring payment status (pause/resume)
export const toggleRecurringPaymentStatus = async (
  id,
  action,
  contextParams
) => {
  const params = addContextParams({}, contextParams);
  const response = await apiClient.patch(
    `/recurring/${id}/${action}`,
    {},
    { params }
  );
  return response.data;
};

// Get recurring payment processing stats
export const getRecurringProcessingStats = async (contextParams) => {
  const params = addContextParams({}, contextParams);
  const response = await apiClient.get("/recurring/stats/processing", {
    params,
  });
  return response.data;
};

// Manually trigger recurring payments processing (for testing)
export const triggerRecurringPaymentsProcessing = async (contextParams) => {
  const params = addContextParams({}, contextParams);
  const response = await apiClient.post(
    "/recurring/process-now",
    {},
    { params }
  );
  return response.data;
};

// Get upcoming recurring payments
export const getUpcomingRecurringPayments = async (
  days = 30,
  contextParams
) => {
  if (isDemoMode()) return demoGetUpcomingRecurring();
  const params = addContextParams({ days }, contextParams);
  const response = await apiClient.get("/recurring/upcoming", { params });
  return response.data;
};

// Enhanced create recurring transaction with validation
export const createRecurringTransaction = async (
  transaction,
  contextParams
) => {
  try {
    // Client-side validation
    // const errors = validateRecurringTransaction(transaction);
    // if (errors.length > 0) {
    //   throw new Error(`Validation errors: ${errors.join(", ")}`);
    // }

    const recurringPayload = {
      start_date: transaction.date,
      frequency: transaction.frequency,
      next_due_date: calculateNextDueDate(
        transaction.date,
        transaction.frequency
      ),
      end_date: transaction.isPermanent ? null : transaction.end_date,
      categoryid: transaction.categoryid,
      amount: parseFloat(transaction.amount),
      vendor: transaction.vendor || "",
      note: transaction.note || "",
      context_type: contextParams.context || "personal",
      group_id: contextParams.groupId || null,
      number_of_payments: transaction.isPermanent
        ? null
        : transaction.recurringcount,
      total_amount: transaction.total_amount || null,
      recurring_count: transaction.isPermanent
        ? null
        : transaction.recurringcount,
      status: "active", // Always start as active
    };

    const params = addContextParams({}, contextParams);
    const response = await apiClient.post("/recurring", recurringPayload, {
      params,
    });
    return response;
  } catch (error) {
    console.error("Error creating recurring transaction:", error);
    throw error;
  }
};

// // Validation function for recurring transactions
// export const validateRecurringTransaction = (transaction) => {
//   const errors = [];

//   if (!transaction.amount || parseFloat(transaction.amount) <= 0) {
//     errors.push("Amount must be greater than 0");
//   }

//   if (
//     !transaction.frequency ||
//     !["DAILY", "WEEKLY", "MONTHLY", "YEARLY"].includes(transaction.frequency)
//   ) {
//     errors.push("Invalid frequency specified");
//   }

//   if (!transaction.category_id) {
//     errors.push("Category is required");
//   }

//   if (!transaction.date) {
//     errors.push("Start date is required");
//   }

//   // Validate start date is not in the past
//   const startDate = new Date(transaction.date);
//   const today = new Date();
//   today.setHours(0, 0, 0, 0);
//   if (startDate < today) {
//     errors.push("Start date cannot be in the past");
//   }

//   if (!transaction.isPermanent) {
//     if (
//       !transaction.recurringcount ||
//       parseInt(transaction.recurringcount) < 1
//     ) {
//       errors.push(
//         "Number of payments must be at least 1 for non-permanent payments"
//       );
//     }

//     if (!transaction.end_date) {
//       errors.push("End date is required for non-permanent payments");
//     } else {
//       // Validate end date is after start date
//       const endDate = new Date(transaction.end_date);
//       if (endDate <= startDate) {
//         errors.push("End date must be after start date");
//       }
//     }
//   }

//   // Validate installment logic
//   if (transaction.isInstallments) {
//     if (
//       !transaction.total_amount ||
//       parseFloat(transaction.total_amount) <= 0
//     ) {
//       errors.push("Total amount is required for installment payments");
//     }

//     const installmentAmount = parseFloat(transaction.amount);
//     const totalAmount = parseFloat(transaction.total_amount);
//     const numPayments = parseInt(transaction.recurringcount);

//     if (Math.abs(installmentAmount * numPayments - totalAmount) > 0.01) {
//       errors.push("Installment amounts do not add up to total amount");
//     }
//   }

//   return errors;
// };

// Calculate next due date utility
export const calculateNextDueDate = (startDate, frequency) => {
  if (!startDate || !frequency) return null;

  const date = new Date(startDate);

  switch (frequency) {
    case "DAILY":
      date.setDate(date.getDate() + 1);
      break;
    case "WEEKLY":
      date.setDate(date.getDate() + 7);
      break;
    case "MONTHLY":
      date.setMonth(date.getMonth() + 1);
      break;
    case "YEARLY":
      date.setFullYear(date.getFullYear() + 1);
      break;
    default:
      return null;
  }

  date.setHours(12, 0, 0, 0);
  return date.toISOString().split("T")[0];
};

// Utility to get payment status display info
export const getPaymentStatusInfo = (payment) => {
  if (payment.status === "completed") {
    return {
      status: "Completed",
      color: "default",
      description: "All payments finished",
    };
  }
  if (payment.status === "paused") {
    return {
      status: "Paused",
      color: "warning",
      description: "Temporarily stopped",
    };
  }

  // Active payments - check timing
  const today = new Date();
  if (payment.next_due_date) {
    const nextDue = new Date(payment.next_due_date);
    const daysUntilDue = Math.ceil((nextDue - today) / (1000 * 60 * 60 * 24));

    if (daysUntilDue <= 0) {
      return {
        status: "Due Today",
        color: "error",
        description: "Payment due now",
      };
    }
    if (daysUntilDue <= 3) {
      return {
        status: "Due Soon",
        color: "warning",
        description: `Due in ${daysUntilDue} days`,
      };
    }
  }

  return {
    status: "Active",
    color: "success",
    description: "Automatically renewing",
  };
};

// Get formatted next payment info
export const getNextPaymentInfo = (payment) => {
  if (!payment.next_due_date) return { text: "Not scheduled", urgent: false };

  const today = new Date();
  const nextDue = new Date(payment.next_due_date);
  const daysUntilDue = Math.ceil((nextDue - today) / (1000 * 60 * 60 * 24));

  if (daysUntilDue === 0) return { text: "Today", urgent: true };
  if (daysUntilDue === 1) return { text: "Tomorrow", urgent: true };
  if (daysUntilDue > 0 && daysUntilDue <= 7)
    return { text: `${daysUntilDue} days`, urgent: false };

  return { text: payment.next_due_date, urgent: false };
};
